import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArchiveIcon from '@mui/icons-material/Archive';
import { ILinkedEmail, ILinkedPhone } from '@sr/dto/dist/src/contacts';
import { LinkToCompany } from 'shared/ui/links/link-to-company';
import { LinkToFile } from 'shared/ui/links/link-to-file';
import { PriceInfoDto } from '@sr/dto';
import { MoreMenu, useMoreMenu } from 'shared/ui/more-menu';
import { formatDateTime } from 'utils/date-format-helpers';
import { formatPerson } from 'utils/person-format-helpers';
import { formatPhone } from 'utils/phone-format-helpers';
import { useSnack } from 'shared/ui/snack';
import useConfirmDialog from 'hooks/confirm-dialog.hook';
import { Delete } from '@mui/icons-material';
import { useDeactivatePrice, useDeletePrice } from 'entities/media/media-price';


type Props = {
  priceInfo: PriceInfoDto;
  onPriceArchived?: (priceId: number) => void;
  onPriceDeleted?: (priceId: number) => void;
}

const MediaPriceInfo = (props: Props) => {
  const { priceInfo: { id, isActive, companyInfo, companyContacts, updatedBy, mainFile, extraFiles, createdAt }, onPriceArchived, onPriceDeleted  } = props;
  const comments1 = '';
  const { openMenu, menuData } = useMoreMenu(extraFiles.map((file) => ({
    element: <LinkToFile file={file} />
  })));

  const deletePrice = useDeletePrice();
  const deactivePrice = useDeactivatePrice();

  const { showError, showSuccess } = useSnack();
  const showConfirm = useConfirmDialog();

  const handleArchiveClick = async (priceId: number) =>
    showConfirm({ content: `Отправить прайс от "${formatDateTime(createdAt)}" в архив?` })
      .then(() => deactivePrice(priceId))
      .then(() => {
        showSuccess('Прайс успешно отправлен в архив');
        onPriceArchived?.(priceId);
      })
      .catch((e) => showError(`Ошибка отправки прайса в архив: ${e.message}`));

  const handleDeleteClick = async (priceId: number) =>
    showConfirm({ content: `Удалить прайс от "${formatDateTime(createdAt)}" из архива?` })
      .then(() => deletePrice(priceId))
      .then(() => {
        showSuccess('Прайс успешно удален из архива');
        onPriceDeleted?.(priceId);
      })
      .catch((e) => {
        console.log(e);
        showError(`Ошибка удаления прайса из архива: ${e.message}`);
      });

  return (
    <Box>
      <Stack direction='row' alignItems={'center'} spacing={1}>
        <Box width={300} sx={{ p: 1 }}>
          <LinkToCompany company={companyInfo} />
        </Box>
        <Box width={200} sx={{ p: 1 }}>
          {
            mainFile ? <LinkToFile file={mainFile} /> : 'нет файла'
          }
          {
            extraFiles.length > 0 && <Stack mt={1} sx={{fontSize: "0.85rem"}}>
              {extraFiles.map(f => <LinkToFile key={f.id} file={f} />)}
            </Stack>
          }
        </Box>
        <Box width={200} sx={{ p: 1 }}>
          <ul>
            {companyContacts.linkedEmails.map((x: ILinkedEmail) => <li key={x.id}>{x.email}</li>)}
          </ul>
          <ul>
            {companyContacts.linkedPhones.map((x: ILinkedPhone) => <li key={x.id}>{formatPhone(x.phone)}</li>)}
          </ul>
        </Box>
        <Box width={200} sx={{ p: 1 }}>
          {formatPerson(updatedBy)}
        </Box>
        <Box>
          {comments1}
        </Box>
        <Box>
          {formatDateTime(createdAt)}
        </Box>
        <Box width={100}>
          {extraFiles.length
            ? <>
              <IconButton
                onClick={(e) => {
                  openMenu(e.currentTarget);
                }}
                edge="end"><MoreVertIcon />
              </IconButton><MoreMenu {...menuData} />
            </>
            : null
          }
        </Box>
        <Box>
          {
            isActive ?
              <Tooltip title="Отправить в архив">
                <IconButton onClick={() => handleArchiveClick(id)} edge="start" color="warning" size="large"><ArchiveIcon />
                </IconButton>
              </Tooltip>
              :
              <Tooltip title="Удалить">
                <IconButton onClick={() => handleDeleteClick(id)} edge="start" color="error" size="large"><Delete />
                </IconButton>
              </Tooltip>
          }
        </Box>
      </Stack>
    </Box>
  );
};

export default MediaPriceInfo;
